import { type HTMLAttributes } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

/**
 * A shimmering placeholder block.
 *
 * `aria-hidden` on every one of them: the loading STATE is announced once, by
 * `RenderGate`'s live region. A screen-reader user meeting twelve anonymous
 * boxes learns nothing and loses their place.
 */
export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-md bg-muted', className)}
      {...props}
    />
  );
}

export interface TableSkeletonProps {
  rows?: number;
  columns: number;
  className?: string;
}

/**
 * A CONTENT-SHAPED table placeholder — same row and column count as the table it
 * stands in for, so the layout does not jump when the real rows arrive. That is
 * the whole reason to prefer a skeleton over a spinner; a skeleton of the wrong
 * shape is just a spinner that lies about it.
 */
export function TableSkeleton({ rows = 5, columns, className }: TableSkeletonProps) {
  return (
    <div className={cn('flex flex-col gap-sm', className)}>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div
          key={rowIndex}
          data-testid="table-skeleton-row"
          className="grid items-center gap-md"
          style={{ gridTemplateColumns: `repeat(${String(columns)}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: columns }, (_, cellIndex) => (
            <Skeleton key={cellIndex} data-testid="table-skeleton-cell" className="h-4 w-full" />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface FormSkeletonProps {
  fields?: number;
  className?: string;
}

/** The same idea for a form: a label bar and a field bar per row. */
export function FormSkeleton({ fields = 4, className }: FormSkeletonProps) {
  return (
    <div className={cn('flex flex-col gap-lg', className)}>
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} data-testid="form-skeleton-field" className="flex flex-col gap-sm">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  );
}
